import { Link } from 'react-router-dom';
import Logo from 'components/Logo';
import useSEO from 'hooks/useSEO';
import { useAuth } from 'context/AuthContext';

const STEPS = [
  { icon: '📝', title: 'Apply', desc: 'Fill out a short application telling us how you plan to promote Safe Dividend Tracker.' },
  { icon: '✅', title: 'Get Approved', desc: "We review applications within 1–2 business days. Once approved, you'll get a unique referral link." },
  { icon: '📣', title: 'Promote', desc: 'Share your link on your blog, YouTube channel, newsletter, or social media.' },
  { icon: '💰', title: 'Earn', desc: 'Earn a commission every time someone signs up and upgrades through your link.' },
];

const FAQS = [
  { q: 'How much can I earn?', a: 'Commissions are set by the program administrator and displayed in your dashboard once approved. Rates vary by plan type.' },
  { q: 'When do I get paid?', a: 'Payouts are processed once your balance hits the minimum threshold. You can request a payout from your dashboard.' },
  { q: 'How long does the cookie last?', a: 'Referral cookies last 60 days by default. If someone clicks your link and upgrades within that window, you earn the commission.' },
  { q: 'What payment methods are supported?', a: 'We currently support PayPal payouts. Bank transfer support is coming soon.' },
  { q: 'Can I see my referral stats?', a: 'Yes — your affiliate dashboard shows clicks, signups, conversions, and earnings in real time.' },
  { q: 'Is there a cost to join?', a: 'No. The affiliate program is completely free to join.' },
];

export default function AffiliatesLandingPage() {
  useSEO({
    title: 'Affiliate Program — Earn by Referring Dividend Investors',
    description: 'Join the Safe Dividend Tracker affiliate program and earn commissions by referring dividend investors to our platform.',
    canonical: '/affiliates',
  });

  const { isAuthenticated } = useAuth();

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Nav */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #E2E8F0', padding: '0 48px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to={isAuthenticated ? '/user-dashboard' : '/'} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <Logo size={36} />
          <span style={{ fontSize: 17, fontWeight: 700, color: '#0F172A' }}>Safe Dividend Tracker</span>
        </Link>
        <div style={{ display: 'flex', gap: 12 }}>
          {isAuthenticated ? (
            <Link to="/user-dashboard" style={{ fontSize: 14, color: '#475569', fontWeight: 500, textDecoration: 'none', padding: '8px 16px', border: '1px solid #E2E8F0', borderRadius: 8 }}>← My Dashboard</Link>
          ) : (
            <Link to="/login" style={{ fontSize: 14, color: '#475569', fontWeight: 500, textDecoration: 'none', padding: '8px 16px', border: '1px solid #E2E8F0', borderRadius: 8 }}>Sign In</Link>
          )}
          {isAuthenticated ? (
            <Link to="/affiliates/dashboard" style={{ fontSize: 14, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#2E7D32,#388E3C)', padding: '8px 18px', borderRadius: 8, textDecoration: 'none' }}>My Affiliate Dashboard →</Link>
          ) : (
            <Link to="/affiliates/apply" style={{ fontSize: 14, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#2E7D32,#388E3C)', padding: '8px 18px', borderRadius: 8, textDecoration: 'none' }}>Apply Now</Link>
          )}
        </div>
      </header>

      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1a3320 100%)', padding: '90px 24px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <span style={{ fontSize: 12, fontWeight: 700, background: 'rgba(34,197,94,0.15)', color: '#22C55E', padding: '4px 14px', borderRadius: 20, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Affiliate Program</span>
          <h1 style={{ fontSize: 52, fontWeight: 900, color: '#fff', lineHeight: 1.1, margin: '20px 0 16px', letterSpacing: '-0.03em' }}>
            Earn by Helping Investors<br />
            <span style={{ color: '#22C55E' }}>Track Their Dividends</span>
          </h1>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, marginBottom: 36 }}>
            Refer your audience to Safe Dividend Tracker and earn a commission every time they upgrade. No cap, no expiry — just passive income from content you're already creating.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {isAuthenticated ? (
              <Link to="/affiliates/dashboard" style={{ padding: '14px 36px', borderRadius: 10, background: '#22C55E', color: '#fff', fontWeight: 700, fontSize: 16, textDecoration: 'none', boxShadow: '0 4px 16px rgba(34,197,94,0.3)' }}>
                Go to My Affiliate Dashboard →
              </Link>
            ) : (
              <>
                <Link to="/affiliates/apply" style={{ padding: '14px 36px', borderRadius: 10, background: '#22C55E', color: '#fff', fontWeight: 700, fontSize: 16, textDecoration: 'none', boxShadow: '0 4px 16px rgba(34,197,94,0.3)' }}>
                  Apply to Be an Affiliate →
                </Link>
                <Link to="/login" state={{ from: '/affiliates/dashboard' }} style={{ padding: '14px 28px', borderRadius: 10, background: 'rgba(255,255,255,0.1)', color: '#fff', fontWeight: 600, fontSize: 15, textDecoration: 'none', border: '1px solid rgba(255,255,255,0.2)' }}>
                  Already an affiliate? Sign in
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', padding: '32px 24px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, textAlign: 'center' }}>
          {[['Free to join', 'No cost, no minimum traffic requirement'],
            ['60-day cookie', 'Long attribution window so you get credit'],
            ['Transparent tracking', 'Real-time clicks & conversions in your dashboard']].map(([title, desc]) => (
            <div key={title}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1a7f4b', marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: 13, color: '#64748B', lineHeight: 1.5 }}>{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '80px 24px' }}>
        <h2 style={{ fontSize: 34, fontWeight: 800, color: '#0F172A', textAlign: 'center', marginBottom: 8, letterSpacing: '-0.02em' }}>How It Works</h2>
        <p style={{ textAlign: 'center', color: '#64748B', fontSize: 15, marginBottom: 52 }}>Four simple steps from application to your first payout.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 28 }}>
          {STEPS.map((s, i) => (
            <div key={s.title} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 16, padding: '28px 22px', position: 'relative' }}>
              <div style={{ position: 'absolute', top: 16, right: 16, width: 26, height: 26, borderRadius: '50%', background: '#1a7f4b', color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</div>
              <div style={{ fontSize: 36, marginBottom: 14 }}>{s.icon}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0F172A', marginBottom: 8 }}>{s.title}</div>
              <div style={{ fontSize: 13, color: '#64748B', lineHeight: 1.6 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Who it's for */}
      <section style={{ background: '#F0FDF4', borderTop: '1px solid #D1FAE5', borderBottom: '1px solid #D1FAE5', padding: '70px 24px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 30, fontWeight: 800, color: '#0F172A', marginBottom: 12, letterSpacing: '-0.02em' }}>Perfect for Dividend Content Creators</h2>
          <p style={{ color: '#374151', fontSize: 15, lineHeight: 1.7, marginBottom: 36 }}>
            If your audience invests in dividend stocks, REITs, or options income — our platform is a natural fit.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
            {['Finance bloggers','YouTube investors','Newsletter writers','Twitter/X finance accounts','Podcast hosts','Reddit community leaders','Financial advisors'].map(tag => (
              <span key={tag} style={{ background: '#fff', border: '1px solid #D1FAE5', color: '#1a7f4b', fontSize: 13, fontWeight: 600, padding: '6px 16px', borderRadius: 20 }}>{tag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ maxWidth: 720, margin: '0 auto', padding: '80px 24px' }}>
        <h2 style={{ fontSize: 30, fontWeight: 800, color: '#0F172A', textAlign: 'center', marginBottom: 40, letterSpacing: '-0.02em' }}>Frequently Asked Questions</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {FAQS.map(({ q, a }) => (
            <div key={q} style={{ border: '1px solid #E2E8F0', borderRadius: 12, padding: '20px 22px' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', marginBottom: 8 }}>{q}</div>
              <div style={{ fontSize: 14, color: '#64748B', lineHeight: 1.7 }}>{a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg,#0F172A,#1a3320)', padding: '64px 24px', textAlign: 'center' }}>
        <h2 style={{ fontSize: 30, fontWeight: 800, color: '#fff', marginBottom: 12 }}>Ready to start earning?</h2>
        <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 28, fontSize: 15 }}>Apply takes 2 minutes. We'll review and get back to you within 1–2 business days.</p>
        {isAuthenticated ? (
          <Link to="/affiliates/dashboard" style={{ display: 'inline-block', padding: '14px 40px', borderRadius: 10, background: '#22C55E', color: '#fff', fontWeight: 700, fontSize: 16, textDecoration: 'none' }}>
            Go to My Affiliate Dashboard →
          </Link>
        ) : (
          <Link to="/affiliates/apply" style={{ display: 'inline-block', padding: '14px 40px', borderRadius: 10, background: '#22C55E', color: '#fff', fontWeight: 700, fontSize: 16, textDecoration: 'none' }}>
            Apply Now — It's Free →
          </Link>
        )}
      </section>

      {/* Footer */}
      <footer style={{ padding: '24px', textAlign: 'center', color: '#94A3B8', fontSize: 12, borderTop: '1px solid #E2E8F0' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap' }}>
          <Link to="/" style={{ color: '#94A3B8', textDecoration: 'none' }}>Home</Link>
          <Link to="/blog" style={{ color: '#94A3B8', textDecoration: 'none' }}>Blog</Link>
          <Link to="/contact" style={{ color: '#94A3B8', textDecoration: 'none' }}>Contact</Link>
          <Link to="/privacy-policy" style={{ color: '#94A3B8', textDecoration: 'none' }}>Privacy</Link>
          <Link to="/terms" style={{ color: '#94A3B8', textDecoration: 'none' }}>Terms</Link>
        </div>
        <p style={{ marginTop: 12 }}>© {new Date().getFullYear()} Safe Dividend Tracker. All rights reserved.</p>
      </footer>
    </div>
  );
}
